import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getOrganizationLogin } from '@/api/auth.ts';
import { externalLinks } from '@/constants/externalLinks.ts';
import queryKeys from '@/constants/queryKeys.ts';
import routes from '@/constants/routes.ts';
import usePersistentPreferencesStore from '@/store/usePersistentPreferencesStore.ts';
import { setReturnPathInStorage } from '@/utils/returnPathStorage.ts';

const useOrganizationLogin = () => {
	const { organizationSlug } = useParams<{ organizationSlug: string }>();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const setReturnPath = usePersistentPreferencesStore(state => state.setReturnPath);
	const { data, isPending, isError } = useQuery({
		queryKey: [queryKeys.getOrganizationLogin, organizationSlug],
		queryFn: () => getOrganizationLogin(organizationSlug || ''),
		enabled: !!organizationSlug,
		retry: false,
	});

	useEffect(() => {
		if (!organizationSlug || isError) {
			navigate(routes.login, { replace: true });
		}
	}, [organizationSlug, isError, navigate]);

	const handleSsoClick = () => {
		const returnPath = externalLinks.spotnana.book;
		setReturnPath(returnPath);
		setReturnPathInStorage(returnPath);
	};

	return {
		data,
		isLoading: !organizationSlug || isPending || isError,
		message: searchParams.get('message'),
		activationToken: searchParams.get('token') || '',
		handleSsoClick,
	};
};

export default useOrganizationLogin;
